import { gql, type TypedDocumentNode } from '@apollo/client';
import { type IActivityDetails, type IFormByActivity, type IReferral, type IReason, type IEmergencyContact } from '../interfaces';

export const ACTIVITY_QUERY: TypedDocumentNode<ActivityQueryData, ActivityQueryVars> = gql`
    query ACTIVITY_QUERY($activityId: Long!) {
        activity(activityId: $activityId) {
            client {
                clientId
                firstName
                lastName
                email
                phones {
                    number
                    phoneTypeCode
                    messageType
                }
                gender
                dateOfBirth
                languagePreference
                address {
                    city
                    state_ProvinceCode
                    countryName
                }    
                contactAddress {
                    addressLine
                    cityName
                    postalZipCode
                    provinceStateName
                    clientAddressDeclined
                    emergencyContactDeclined
                }
                clientEmergencyContacts {
                    clientEmergencyContactId
                    firstName
                    lastName
                    phoneNumber
                    contactRelationshipId
                    otherRelationship
                    status
                }
            }
            preAssessments {
                additionalIndividualId
                activityId
                clientName
                status
                url
            }
            additionalParticipants {
                id 
                firstName,
                lastName,
                relationType,
                fullName,
                dateOfBirth
            }
            sessionInfo {
                modelId
                totalHoursProvided
                totalHoursRemaining
                maxCapHours
            }
            activityStatusCode
            service
            isAvailableToProvider
            clientStatusCode
            eligibility
            clientMaxHours
            clientUsedHours
            clientAvailableHours
            clientMaxHoursStartDate
            clientMaxHoursEndDate
            isHourlyContract
            modality
            targetedSystem {
                systemCode
            }
            organization {
                name
                organizationId
            }
            providerId
            presentingIssue
            activityId
            sessions {
                duration
                sessionId
                sessionStatus
                startDateTimeUTC
                sessionStatusId
                providerHubAppointmentStatusId
                clientReservationPhoneNumber
            }
            openDate
            activityClosedTime
            urgencyLevel
            urgencyLevelString
            pcShowFamilyHours
            familyTotalAllocatedHours
            familyTotalRemainingHours
            familyEligibilityHours {
                eligibilityCategoryDescription
                allocatedHours
                remainingHours
                eligibilityCategoryId
            }
            familyMembers {
                id
                firstName
                lastName
            }
            serviceFor
            indigenousLabel
            solutionCode
            followUpDate
            sessionBooking {
                allowed
                prohibitionReasonCode
            }
            caseDetailHours {
                clientRemainingHours
                numberOfHoursUsed
                totalHoursAvailable
                numberOfMaxHours
                approvedHours
            }
            serviceForms {
                clientPhoneNumber
                cac_Notes
                cacGuidelines
                risks
            }
            contractAllowsAdditionalHours
            assessmentsScores {
                PHQ9 {
                    date
                    score
                    formId
                    sessionId
                }
            }
            firstAppointmentAssessmentFormNames {
                templateName
                mandatory
            }
        }
        formsByActivity(activityId: $activityId) {
            id
            completed
            templateName
            updatedBy
            createdBy
            sessionId
            updatedOn
            createdOn
            stage
            preAssessmentStageStatus
            postAssessmentStageStatus
        }
        referral(activityId: $activityId) {
            referralAccepted
            clientCallingPreference
            serviceFormNotes
        }
    }
`

export const UPDATE_EMERGENCY_CONTACTS_MUTATION: TypedDocumentNode<UpdateEmergencyContactsResponse, ClientEmergencyContactVars> = gql`
    mutation UPDATE_EMERGENCY_CONTACTS_MUTATION(
        $contacts: [ClientEmergencyContactDTOInput!]
        $clientId: Int!
        $activityId: Int!
        $providerId: Int!
        $emergencyContactDeclined: Boolean!
    ) {
        updateClientEmergencyContacts(
            clientEmergencyContactInfo: {
                contacts: $contacts
                clientId: $clientId
                activityId: $activityId
                providerId: $providerId
                emergencyContactDeclined: $emergencyContactDeclined
            }
        )
    }
`

export const UPDATE_CLIENT_CONTACT_ADDRESS: TypedDocumentNode<UpdateContactAddressResponse, ClientContactAddressVars> = gql`
    mutation UPDATE_CLIENT_CONTACT_ADDRESS(
        $providerId: Int!
        $activityId: Int!
        $clientId: Int!
        $addressLine: String
        $cityName: String
        $postalZipCode: String
        $provinceStateName: String
        $clientAddressDeclined: Boolean!
    ) {
        updateClientEmergencyAddress(
            address: {
                providerId: $providerId
                activityId: $activityId
                addressLine: $addressLine
                cityName: $cityName
                postalZipCode: $postalZipCode
                provinceStateName: $provinceStateName
                clientAddressDeclined: $clientAddressDeclined
                clientId: $clientId
            }
        )
    }
`

export interface UpdateEmergencyContactsResponse {
    updateClientEmergencyContacts: string
}

export interface UpdateContactAddressResponse {
    updateClientEmergencyAddress: string
}

export interface ClientEmergencyContactVars {
    contacts: IEmergencyContact[]
    clientId: number
    activityId: number
    providerId: number
    emergencyContactDeclined?: boolean
}

export interface ClientContactAddressVars {
    providerId: number
    activityId: number
    clientId: number
    addressLine: string
    cityName: string
    postalZipCode: string
    provinceStateName: string
    clientAddressDeclined: boolean
}

export interface ActivityQueryData {
    activity: IActivityDetails
    formsByActivity: IFormByActivity[]
    referral: IReferral
}

export interface ActivityQueryVars {
    activityId: number
}


export const CLOSE_ACTIVITY_MUTATION = gql`
    mutation CLOSE_ACTIVITY_MUTATION($activityId: Long!, $closureDate: DateTime, $providerId: Long!) {
        closeActivity(activityId: $activityId, closureDate: $closureDate, providerId: $providerId)
    }
`

export const DECLINE_REASONS_QUERY: TypedDocumentNode<DeclineReasonsQueryData, DeclineReasonsQueryVars> = gql`
    query DECLINE_REASONS_QUERY {
        appointmentDeclineReasons {
            id
            description 
        }
    }
`

export const DECLINE_WOS_INFO_QUERY: TypedDocumentNode<DeclineWosInfoQueryData, DeclineWosInfoQueryVars> = gql`
    query DECLINE_WOS_INFO_QUERY($activityId: Long!)  {
        declineWosInfo(activityId: $activityId) {
            outcome
            declinePreAssessmentReason             
        }
    }
`
export const CONTACT_RELATIONSHIP_QUERY: TypedDocumentNode<ContactRelationshipData, { language: string }> = gql`
    query CONTACT_RELATIONSHIP_QUERY ($language: String) {
        contactRelationships(language: $language) {
            contactRelationshipId,
            description,
            status
        }
    }
`

interface ContactRelationshipData {
    contactRelationships: {
        contactRelationshipId: number,
        description: string,
        status: string,
    }[]
}

interface DeclineReasonsQueryVars { }

interface DeclineReasonsQueryData {
    appointmentDeclineReasons: IReason[]
}

interface DeclineWosInfoQueryVars {
    activityId: number
}

interface DeclineWosInfoQueryData {
    declineWosInfo: DeclineWosInfo
}

export interface DeclineWosInfo {
    outcome: string,
    declinePreAssessmentReason: string
}


export const IS_ACTIVITY_ACCEPTED_QUERY: TypedDocumentNode<IIsActivityAcceptedQueryData, IIsActivityAcceptedQueryVars>  = gql`
    query IS_ACTIVITY_ACCEPTED_QUERY($activityId: Long!, $providerId: Long!) {
        isActivityAcceptedByProvider(activityId: $activityId, providerId: $providerId)
    }
`
export interface IIsActivityAcceptedQueryVars {
    activityId: number;
    providerId: number;
}

export interface IIsActivityAcceptedQueryData {
    isActivityAcceptedByProvider: boolean
};

